import React, { useState, useEffect } from 'react'
import { useApiClient } from '../../hooks/useApiClient'
import { useTheme } from '../../hooks/useTheme'
import { getStatusColors } from '../../utils/statusColorMap'
import { formatDateDisplay } from '../../utils/dateHelpers'

interface Tarifa {
  id?: string
  nombre: string
  tarifa_simple: number
  tarifa_multiple: number | null
  fecha_inicio: string
  fecha_fin: string
  dias_semana: number[] | null
  activa: boolean
}

interface EspacioTarifasManagerProps {
  espacioId: string
  empresaId: number
}

const EspacioTarifasManager: React.FC<EspacioTarifasManagerProps> = ({ espacioId, empresaId }) => {
  const { colors } = useTheme()
  const { apiClient, loading, error, setLoading, setError } = useApiClient()
  const [tarifas, setTarifas] = useState<Tarifa[]>([])
  const [showForm, setShowForm] = useState(false)
  const [editingTarifa, setEditingTarifa] = useState<Tarifa | null>(null)
  const [form, setForm] = useState<Tarifa>({
    nombre: '',
    tarifa_simple: 0,
    tarifa_multiple: null,
    fecha_inicio: new Date().toISOString().split('T')[0],
    fecha_fin: new Date().toISOString().split('T')[0],
    dias_semana: null,
    activa: true
  })

  useEffect(() => {
    fetchTarifas()
  }, [espacioId])

  const fetchTarifas = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.get(`/api/admin-protected/espacios/${espacioId}/tarifas`)
      setTarifas(Array.isArray(response.data) ? response.data : [])
    } catch (err: any) {
      console.error('Error fetching tarifas:', err)
      setError('Error al cargar tarifas')
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setForm({
      nombre: '',
      tarifa_simple: 0,
      tarifa_multiple: null,
      fecha_inicio: new Date().toISOString().split('T')[0],
      fecha_fin: new Date().toISOString().split('T')[0],
      dias_semana: null,
      activa: true
    })
    setEditingTarifa(null)
  }

  const handleEdit = (tarifa: Tarifa) => {
    setEditingTarifa(tarifa)
    setForm(tarifa)
    setShowForm(true)
  }

  const handleSubmit = async () => {
    // No prevenir default ya que no es un formulario

    if (!form.nombre.trim()) {
      setError('El nombre de la tarifa es requerido')
      return
    }

    if (form.tarifa_simple <= 0) {
      setError('La tarifa simple debe ser mayor a 0')
      return
    }

    try {
      setLoading(true)
      setError(null)

      const formData = {
        nombre: form.nombre,
        tarifa_simple: form.tarifa_simple,
        tarifa_multiple: form.tarifa_multiple,
        fecha_inicio: form.fecha_inicio,
        fecha_fin: form.fecha_fin,
        dias_semana: form.dias_semana,
        activa: form.activa
      }

      if (editingTarifa && editingTarifa.id) {
        await apiClient.put(
          `/api/admin-protected/espacios/${espacioId}/tarifas/${editingTarifa.id}`,
          formData
        )
      } else {
        await apiClient.post(
          `/api/admin-protected/espacios/${espacioId}/tarifas`,
          formData
        )
      }

      setShowForm(false)
      resetForm()
      await fetchTarifas()
    } catch (err: any) {
      console.error('Error saving tarifa:', err)
      setError('Error al guardar tarifa')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (tarifaId: string) => {
    if (confirm('¿Estás seguro de que deseas eliminar esta tarifa?')) {
      try {
        setLoading(true)
        setError(null)
        await apiClient.delete(`/api/admin-protected/espacios/${espacioId}/tarifas/${tarifaId}`)
        await fetchTarifas()
      } catch (err: any) {
        console.error('Error deleting tarifa:', err)
        setError('Error al eliminar tarifa')
      } finally {
        setLoading(false)
      }
    }
  }

  const diasSemanaLabels: Record<number, string> = {
    0: 'Lunes',
    1: 'Martes',
    2: 'Miércoles',
    3: 'Jueves',
    4: 'Viernes',
    5: 'Sábado',
    6: 'Domingo'
  }

  return (
    <div className="border rounded-lg p-4" style={{ borderColor: colors.primary }}>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold" style={{ color: colors.primary }}>
          Tarifas Personalizadas del Espacio
        </h3>
        {!showForm && (
          <button
            onClick={() => {
              resetForm()
              setShowForm(true)
            }}
            className="px-4 py-2 rounded-lg text-white transition-colors"
            style={{ backgroundColor: colors.primary }}
          >
            + Agregar Tarifa
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Formulario */}
      {showForm && (
        <div className="mb-6 p-4 border rounded-lg" style={{ borderColor: colors.primaryLight }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Nombre */}
            <div>
              <label className="block text-sm font-medium mb-1">Nombre *</label>
              <input
                type="text"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Ej: Tarifa Fin de Semana"
              />
            </div>

            {/* Tarifa Simple */}
            <div>
              <label className="block text-sm font-medium mb-1">Tarifa Simple (1 unidad) *</label>
              <input
                type="number"
                value={form.tarifa_simple}
                onChange={(e) => setForm({ ...form, tarifa_simple: parseFloat(e.target.value) })}
                className="w-full border rounded-lg px-3 py-2"
                step="0.01"
                min="0"
              />
            </div>

            {/* Tarifa Múltiple */}
            <div>
              <label className="block text-sm font-medium mb-1">Tarifa Múltiple (2+ unidades) <span className="text-gray-500">(Opcional)</span></label>
              <input
                type="number"
                value={form.tarifa_multiple || ''}
                onChange={(e) => setForm({ ...form, tarifa_multiple: e.target.value ? parseFloat(e.target.value) : null })}
                className="w-full border rounded-lg px-3 py-2"
                step="0.01"
                min="0"
              />
              <p className="text-xs text-gray-500 mt-1">Si no se especifica, se usa la tarifa simple</p>
            </div>

            {/* Fecha Inicio */}
            <div>
              <label className="block text-sm font-medium mb-1">Fecha Inicio *</label>
              <input
                type="date"
                value={form.fecha_inicio}
                onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>

            {/* Fecha Fin */}
            <div>
              <label className="block text-sm font-medium mb-1">Fecha Fin *</label>
              <input
                type="date"
                value={form.fecha_fin}
                onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>

            {/* Estado */}
            <div>
              <label className="block text-sm font-medium mb-1">Estado</label>
              <select
                value={form.activa ? 'activa' : 'inactiva'}
                onChange={(e) => setForm({ ...form, activa: e.target.value === 'activa' })}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="activa">Activa</option>
                <option value="inactiva">Inactiva</option>
              </select>
            </div>
          </div>

          {/* Días de la Semana */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Días de la Semana <span className="text-gray-500">(Opcional - Si está vacío aplica todos los días)</span></label>
            <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
              {Object.entries(diasSemanaLabels).map(([dia, label]) => (
                <label key={dia} className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.dias_semana?.includes(parseInt(dia)) || false}
                    onChange={(e) => {
                      const dias = form.dias_semana || []
                      if (e.target.checked) {
                        setForm({ ...form, dias_semana: [...dias, parseInt(dia)] })
                      } else {
                        setForm({ ...form, dias_semana: dias.filter(d => d !== parseInt(dia)) })
                      }
                    }}
                    className="w-4 h-4 rounded"
                  />
                  <span className="ml-2 text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Botones */}
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => {
                setShowForm(false)
                resetForm()
              }}
              className="px-4 py-2 border rounded-lg"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="px-4 py-2 rounded-lg text-white transition-colors"
              style={{ backgroundColor: colors.primary, opacity: loading ? 0.7 : 1 }}
            >
              {loading ? 'Guardando...' : editingTarifa ? 'Actualizar' : 'Crear'}
            </button>
          </div>
        </div>
      )}

      {/* Lista de Tarifas */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: `2px solid ${colors.primary}` }}>
              <th className="text-left py-3 px-2">Nombre</th>
              <th className="text-center py-3 px-2">Tarifa Simple</th>
              <th className="text-center py-3 px-2">Tarifa Múltiple</th>
              <th className="text-center py-3 px-2">Rango de Fechas</th>
              <th className="text-center py-3 px-2">Días</th>
              <th className="text-center py-3 px-2">Estado</th>
              <th className="text-center py-3 px-2">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {tarifas.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-4 text-gray-500">
                  No hay tarifas personalizadas
                </td>
              </tr>
            ) : (
              tarifas.map((tarifa) => (
                <tr key={tarifa.id} style={{ borderBottom: `1px solid ${colors.primaryLight}` }}>
                  <td className="py-3 px-2 font-medium">{tarifa.nombre}</td>
                  <td className="text-center py-3 px-2">${tarifa.tarifa_simple.toLocaleString('es-CL')}</td>
                  <td className="text-center py-3 px-2">
                    {tarifa.tarifa_multiple ? `$${tarifa.tarifa_multiple.toLocaleString('es-CL')}` : '-'}
                  </td>
                  <td className="text-center py-3 px-2 text-xs">
                    {formatDateDisplay(tarifa.fecha_inicio)} -
                    <br />
                    {formatDateDisplay(tarifa.fecha_fin)}
                  </td>
                  <td className="text-center py-3 px-2 text-xs">
                    {tarifa.dias_semana && tarifa.dias_semana.length > 0 ? (
                      <span>{tarifa.dias_semana.map(d => diasSemanaLabels[d]).join(', ')}</span>
                    ) : (
                      <span className="text-gray-500">Todos</span>
                    )}
                  </td>
                  <td className="text-center py-3 px-2">
                    <span
                      className="px-2 py-1 rounded text-xs text-white"
                      style={{ backgroundColor: tarifa.activa ? colors.primary : '#999' }}
                    >
                      {tarifa.activa ? 'Activa' : 'Inactiva'}
                    </span>
                  </td>
                  <td className="text-center py-3 px-2">
                    <button
                      onClick={() => handleEdit(tarifa)}
                      className="text-blue-600 hover:text-blue-800 mr-2"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => tarifa.id && handleDelete(tarifa.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default EspacioTarifasManager
